import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { userAPI } from '../../services/api';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { MessageSquare, Send, Edit2, Trash2, Ban, ArrowLeft, Users, Plus, Smile, UserPlus, UserMinus, LogOut } from 'lucide-react';

const REACTION_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '💯'];

export const MessagesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openReaction, setOpenReaction] = useState(null); // message id
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // {cid: {uid: {name, ts}}}
  const endRef = useRef(null);
  const selectedRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const handleWsMessage = useCallback((data) => {
    if (!data || !data.type) return;
    const cur = selectedRef.current;

    if (data.type === 'message_new') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        // auto-mark read
        userAPI.markConversationRead(data.conversation_id).catch(() => {});
      }
      // refresh convs list to update preview/unread
      loadConversations();
    } else if (data.type === 'message_edit') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message.id ? data.message : m));
      }
    } else if (data.type === 'message_delete') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, deleted: true, text: '(deleted)' } : m));
      }
    } else if (data.type === 'message_reaction') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message.id ? { ...m, reactions: data.message.reactions } : m));
      }
    } else if (data.type === 'typing') {
      const { conversation_id, user_id, name, is_typing } = data;
      setTypingUsers((prev) => {
        const next = { ...prev };
        const forConv = { ...(next[conversation_id] || {}) };
        if (is_typing) {
          forConv[user_id] = { name, ts: Date.now() };
        } else {
          delete forConv[user_id];
        }
        next[conversation_id] = forConv;
        return next;
      });
    } else if (data.type === 'conversation_created' || data.type === 'conversation_updated' || data.type === 'conversation_removed') {
      loadConversations();
      if (data.type === 'conversation_removed' && cur && cur.id === data.conversation_id) {
        setSelected(null);
        setMessages([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { send: wsSend } = useWebSocket(handleWsMessage);

  // Prune stale typing entries every 3s
  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const next = {};
        const now = Date.now();
        for (const cid of Object.keys(prev)) {
          const forConv = {};
          for (const uid of Object.keys(prev[cid])) {
            if (now - prev[cid][uid].ts < 5000) forConv[uid] = prev[cid][uid];
          }
          if (Object.keys(forConv).length) next[cid] = forConv;
        }
        return next;
      });
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const loadConversations = async () => {
    try {
      const res = await userAPI.listConversations();
      setConversations(res.data);
    } catch (e) { /* noop */ }
  };

  const load = async () => {
    await loadConversations();
    const params = new URLSearchParams(location.search);
    const withUid = params.get('with');
    if (withUid && !selected) {
      try {
        const c = await userAPI.createConversation({ type: 'dm', participants: [withUid] });
        openConv(c.data);
        await loadConversations();
      } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed to open chat'); }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [location.search]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openConv = async (c) => {
    setSelected(c);
    try {
      const res = await userAPI.listMessages(c.id);
      setMessages(res.data);
      await loadConversations();
    } catch (e) { toast.error('Failed'); }
  };

  const send = async () => {
    if (!text.trim() || !selected) return;
    setBusy(true);
    try {
      if (editing) {
        const res = await userAPI.editMessage(editing.id, text.trim());
        setMessages(messages.map((m) => m.id === editing.id ? res.data : m));
        setEditing(null);
      } else {
        const res = await userAPI.sendMessage(selected.id, text.trim());
        setMessages((prev) => {
          if (prev.some((m) => m.id === res.data.id)) return prev;
          return [...prev, res.data];
        });
      }
      setText('');
      // clear typing immediately
      wsSend({ type: 'typing', conversation_id: selected.id, is_typing: false });
      loadConversations();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  const startEdit = (m) => { setEditing(m); setText(m.text); };

  const del = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await userAPI.deleteMessage(m.id);
      setMessages(messages.map((x) => x.id === m.id ? { ...x, text: '(deleted)', deleted: true } : x));
    } catch (e) { toast.error('Failed'); }
  };

  const react = async (m, emoji) => {
    setOpenReaction(null);
    try {
      const res = await userAPI.reactMessage(m.id, emoji);
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, reactions: res.data.reactions } : x));
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const blockOther = async () => {
    const other = selected.participants.find((p) => p.user_id !== user.id);
    if (!other) return;
    if (!window.confirm(`Block ${other.name}? They won't be able to message you.`)) return;
    try {
      await userAPI.blockUser(other.user_id);
      toast.success('Blocked');
      setSelected(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (!selected) return;
    // Send typing indicator (throttled by socket-ready)
    wsSend({ type: 'typing', conversation_id: selected.id, is_typing: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      wsSend({ type: 'typing', conversation_id: selected.id, is_typing: false });
    }, 3000);
  };

  const currentTyping = selected ? Object.values(typingUsers[selected.id] || {}).filter((t) => t.name) : [];
  const typingLabel = currentTyping.length === 0 ? '' :
    currentTyping.length === 1 ? `${currentTyping[0].name} is typing…` :
    `${currentTyping.map((t) => t.name).join(', ')} are typing…`;

  return (
    <UserLayout>
      <div className="grid gap-4 md:grid-cols-[320px_1fr] h-[calc(100vh-8rem)]" data-testid="messages-page">
        <Card className={selected ? 'hidden md:block' : ''}>
          <CardContent className="pt-4 h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h2>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowNewGroup(true)} data-testid="new-group-btn">
                <Plus className="h-3 w-3 mr-1" /> Group
              </Button>
            </div>
            {conversations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No conversations yet. Start one from a user&apos;s profile, Contacts, or create a group.</p>
            ) : (
              <div className="space-y-1">
                {conversations.map((c) => {
                  const someTyping = Object.values(typingUsers[c.id] || {}).length > 0;
                  return (
                    <button key={c.id} onClick={() => openConv(c)}
                      className={`w-full text-left p-3 rounded-lg hover:bg-muted transition-colors ${selected?.id === c.id ? 'bg-muted' : ''}`}
                      data-testid={`conv-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium truncate flex items-center gap-1">
                          {c.type === 'group' && <Users className="h-3 w-3 flex-shrink-0" />}
                          {c.name || '(unnamed)'}
                        </p>
                        {c.unread_count > 0 && <Badge className="bg-blue-500 text-white">{c.unread_count}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {someTyping ? <span className="italic text-blue-500">typing…</span> : (c.last_message_preview || 'No messages yet')}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={!selected ? 'hidden md:flex md:items-center md:justify-center' : 'flex flex-col'}>
          {!selected ? (
            <p className="text-muted-foreground">Select a conversation to start</p>
          ) : (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelected(null)}><ArrowLeft className="h-4 w-4" /></Button>
                  <div>
                    <p className="font-semibold" data-testid="conv-title">{selected.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {selected.type === 'dm' ? 'Direct message' : `${selected.participants.length} members`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selected.type === 'group' && (
                    <Button variant="outline" size="sm" onClick={() => setShowManage(true)} data-testid="manage-group-btn">
                      <Users className="h-3 w-3 mr-1" /> Manage
                    </Button>
                  )}
                  {selected.type === 'dm' && (
                    <Button variant="outline" size="sm" onClick={blockOther} data-testid="block-btn"><Ban className="h-3 w-3 mr-1" /> Block</Button>
                  )}
                </div>
              </div>

              <CardContent className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    const reactions = m.reactions || {};
                    const reactionEntries = Object.entries(reactions);
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-lg p-3 group relative ${mine ? 'bg-blue-500/15 border border-blue-500/25' : 'bg-muted'}`} data-testid={`msg-${m.id}`}>
                          {!mine && selected.type === 'group' && <p className="text-xs font-medium mb-1">{m.sender_name}</p>}
                          <p className={`text-sm whitespace-pre-wrap break-words ${m.deleted ? 'italic text-muted-foreground' : ''}`}>{m.text}</p>
                          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                            {formatDate(m.created_at)}
                            {m.edited_at && <span>· edited</span>}
                            {!m.deleted && (
                              <div className={`ml-2 flex gap-1 ${openReaction === m.id ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                                <button onClick={() => setOpenReaction(openReaction === m.id ? null : m.id)} data-testid={`react-btn-${m.id}`}><Smile className="h-3 w-3 inline" /></button>
                                {mine && (
                                  <>
                                    <button onClick={() => startEdit(m)} data-testid={`edit-btn-${m.id}`}><Edit2 className="h-3 w-3 inline" /></button>
                                    <button onClick={() => del(m)} data-testid={`del-btn-${m.id}`}><Trash2 className="h-3 w-3 inline" /></button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          {openReaction === m.id && (
                            <div className="absolute z-10 -top-10 left-0 bg-background border border-border rounded-full shadow-lg px-2 py-1 flex gap-1">
                              {REACTION_OPTIONS.map((emo) => (
                                <button key={emo} onClick={() => react(m, emo)} className="hover:scale-125 transition-transform text-base" data-testid={`react-${m.id}-${emo}`}>{emo}</button>
                              ))}
                            </div>
                          )}
                          {reactionEntries.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {reactionEntries.map(([emo, uids]) => {
                                const active = uids.includes(user.id);
                                return (
                                  <button
                                    key={emo}
                                    onClick={() => react(m, emo)}
                                    className={`text-xs px-1.5 py-0.5 rounded-full border transition-colors ${active ? 'bg-blue-500/20 border-blue-500/40' : 'bg-background border-border hover:bg-muted'}`}
                                    data-testid={`reaction-chip-${m.id}-${emo}`}
                                  >
                                    {emo} {uids.length}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
              </CardContent>

              {typingLabel && (
                <div className="px-4 pb-1 text-xs italic text-muted-foreground" data-testid="typing-indicator">{typingLabel}</div>
              )}

              <div className="p-3 border-t border-border">
                {editing && (
                  <div className="flex items-center justify-between text-xs mb-2 px-2 py-1 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400">
                    <span>Editing message</span>
                    <button onClick={() => { setEditing(null); setText(''); }}>Cancel</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea rows={1} value={text} onChange={onTextChange}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type a message..." className="resize-none" data-testid="msg-input" />
                  <Button onClick={send} disabled={busy || !text.trim()} data-testid="send-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <NewGroupDialog open={showNewGroup} onOpenChange={setShowNewGroup} onCreated={(c) => { loadConversations(); openConv(c); }} />
      <ManageGroupDialog
        open={showManage}
        onOpenChange={setShowManage}
        conversation={selected}
        currentUserId={user?.id}
        onChanged={async () => {
          await loadConversations();
          if (selected) {
            const fresh = (await userAPI.listConversations()).data.find((c) => c.id === selected.id);
            if (fresh) setSelected(fresh);
          }
        }}
        onLeft={() => { setSelected(null); setMessages([]); loadConversations(); }}
      />
    </UserLayout>
  );
};

// ============ New Group Dialog ============
const NewGroupDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setName(''); setSelected([]); setSearch(''); return; }
    (async () => {
      try {
        const res = await userAPI.getDirectory(search);
        setUsers(res.data);
      } catch (e) { /* noop */ }
    })();
  }, [open, search]);

  const toggle = (uid) => {
    setSelected((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const create = async () => {
    if (!name.trim()) return toast.error('Group name required');
    if (selected.length < 2) return toast.error('Pick at least 2 people');
    setBusy(true);
    try {
      const res = await userAPI.createConversation({ type: 'group', name: name.trim(), participants: selected });
      toast.success('Group created');
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-group-dialog">
        <DialogHeader><DialogTitle>Create a group</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="group-name-input" />
          <Input placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="group-search-input" />
          <div className="max-h-64 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
            {users.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No users found</p> :
              users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer" data-testid={`group-user-${u.id}`}>
                  <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">@{u.username || u.email?.split('@')[0] || 'user'}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.name || 'User'}</p>
                  </div>
                </label>
              ))
            }
          </div>
          <p className="text-xs text-muted-foreground">Selected: {selected.length}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={busy} data-testid="create-group-submit"><Plus className="h-4 w-4 mr-1" /> Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Manage Group Dialog ============
const ManageGroupDialog = ({ open, onOpenChange, conversation, currentUserId, onChanged, onLeft }) => {
  const [directory, setDirectory] = useState([]);
  const [search, setSearch] = useState('');
  const isCreator = conversation?.created_by === currentUserId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await userAPI.getDirectory(search);
        setDirectory(res.data);
      } catch (e) { /* noop */ }
    })();
  }, [open, search]);

  if (!conversation) return null;

  const memberIds = new Set(conversation.participants.map((p) => p.user_id));
  const addable = directory.filter((u) => !memberIds.has(u.id));

  const addMember = async (uid) => {
    try {
      await userAPI.addGroupMember(conversation.id, uid);
      toast.success('Member added');
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await userAPI.removeGroupMember(conversation.id, uid);
      toast.success('Member removed');
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const leave = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await userAPI.leaveGroup(conversation.id);
      toast.success('Left group');
      onOpenChange(false);
      onLeft?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-group-dialog">
        <DialogHeader><DialogTitle>Manage &quot;{conversation.name}&quot;</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Members ({conversation.participants.length})</p>
            <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
              {conversation.participants.map((p) => (
                <div key={p.user_id} className="flex items-center justify-between gap-2 p-1.5 rounded hover:bg-muted">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {p.name} {p.user_id === conversation.created_by && <Badge variant="secondary" className="ml-1 text-[10px]">Creator</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                  </div>
                  {isCreator && p.user_id !== currentUserId && (
                    <Button variant="ghost" size="icon" onClick={() => removeMember(p.user_id)} data-testid={`remove-member-${p.user_id}`}>
                      <UserMinus className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isCreator && (
            <div>
              <p className="text-sm font-medium mb-2">Add members</p>
              <Input placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" data-testid="manage-search-input" />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                {addable.length === 0 ? <p className="text-xs text-muted-foreground text-center py-2">No one else to add</p> :
                  addable.slice(0, 20).map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-2 p-1.5 rounded hover:bg-muted">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">@{u.username || u.email?.split('@')[0] || 'user'}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => addMember(u.id)} data-testid={`add-member-${u.id}`}>
                        <UserPlus className="h-4 w-4 text-blue-500" />
                      </Button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          <Button variant="outline" onClick={leave} className="w-full text-red-500 hover:text-red-600" data-testid="leave-group-btn">
            <LogOut className="h-4 w-4 mr-2" /> Leave group
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
